import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { RoleMapToolbar } from './RoleMapToolbar';
import { RoleMapBulkBar } from './RoleMapBulkBar';
import { RoleMapTable } from './RoleMapTable';
import { RoleMapPagination } from './RoleMapPagination';
import { RoleMapAddModal } from './RoleMapAddModal';
import { RoleMapDeleteConfirmModal } from './RoleMapDeleteConfirmModal';
import { RoleMapRepointModal } from './RoleMapRepointModal';
import { RoleMapScopeDiffModal } from './RoleMapScopeDiffModal';
import { RoleMapScopePanel } from './RoleMapScopePanel';
import {
    NoStandardRolesEmptyState,
    RoleMapFilteredEmptyState,
    RoleMapLoadingSkeleton,
    RoleMapNoRowsEmptyState,
} from './EmptyStates';
import type { RoleMapTableVM } from './useRoleMapTable';

/**
 * EAA-1841: the **list** view of "Liên kết vai trò" — the flat, sortable,
 * filterable, paginated table with row selection and bulk actions (design frame
 * 49, `role-map-table.html`).
 *
 * Lifted out of the pre-merge `RoleMapPage` unchanged apart from losing the page
 * chrome, which the merged page now owns for both views. Everything the bulk bar
 * depends on — including the "Mặc định has no bulk endpoints" degrade — is
 * untouched.
 *
 * EAA-1841: the Add-link modal and the undo banner live HERE rather than on the
 * merged page, because both are driven by `useRoleMapTable`, whose queries are
 * disabled while the board view is on screen — mounted from the page they would
 * render with an empty app list and a permanently disabled submit. The board has
 * its own equivalents anyway: per-role `+ vai trò` pickers and the editor card's
 * own undo banner (design frame 42 has no global "Thêm liên kết" at all).
 */
export function RoleMapListView({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [deleteOpen, setDeleteOpen] = useState(false);

    const status = vm.syncStatusQuery.data;
    const scopeLabel =
        vm.scope === 'default'
            ? t('console.roleMap.listView.scopeDefault')
            : (vm.actingOrgName ?? t('console.roleMap.listView.scopeOrgFallback'));

    let body;
    if (vm.loading) {
        body = <RoleMapLoadingSkeleton />;
    } else if (vm.standardRoles.length === 0 && vm.totalLinks === 0) {
        body = <NoStandardRolesEmptyState onCreateLevel={() => navigate('/console/hierarchy')} />;
    } else if (vm.filteredCount === 0 && vm.filtersActive) {
        body = <RoleMapFilteredEmptyState onClear={vm.clearFilters} />;
    } else if (vm.filteredCount === 0) {
        body = (
            <RoleMapNoRowsEmptyState
                scopeLabel={scopeLabel}
                canAdd={vm.canManageCurrentScope}
                onAdd={vm.openAdd}
            />
        );
    } else {
        body = (
            <>
                <RoleMapTable vm={vm} />
                <RoleMapPagination vm={vm} />
            </>
        );
    }

    return (
        // EAA-1817: was a bare fragment directly inside `.rm-card`, which gave
        // toolbar/bulk-bar/banner/table/pagination their `gap` rhythm via ITS
        // OWN flex column. `.rm-card` is gone (page chrome moved onto
        // `PageHeader`/`SectionCard`) — this wrapper owns that same rhythm now,
        // self-contained regardless of what ends up wrapping this component.
        <div className="rm-list-body">
            <RoleMapToolbar vm={vm} />
            <RoleMapScopePanel vm={vm} />

            {/* EAA-1837: the honest degrade. Row selection and every bulk
                action are unavailable in the Mặc định slice because the
                endpoints are org-scoped and skip `organization_id IS NULL`
                rows — say why, rather than showing a bar whose every call
                would come back empty. */}
            {vm.bulkDisabledReason && <p className="rm-bulk-note">{vm.bulkDisabledReason}</p>}

            <RoleMapBulkBar vm={vm} onRequestDelete={() => setDeleteOpen(true)} />

            {status?.status === 'degraded' && (
                <div className="portal-console-banner is-degraded" role="alert">
                    <strong>
                        {t('console.roleMap.listView.degradedBannerText', {
                            count: status.paused_count,
                        })}
                    </strong>
                </div>
            )}

            {/* EAA-1817: the one region that grows/scrolls inside `.rm-list-body`
                — toolbar/bulk-bar/banner above stay their natural height (default
                flex-grow:0), this absorbs the rest per the fit-to-shell rule. */}
            <div className="rm-list-scroll">{body}</div>

            <RoleMapDeleteConfirmModal
                open={deleteOpen}
                groups={vm.selectedGroups}
                linkCount={vm.selectedIdCount}
                pending={vm.bulkBusy}
                onCancel={() => setDeleteOpen(false)}
                onConfirm={() => {
                    setDeleteOpen(false);
                    vm.applyBulkDelete();
                }}
            />

            <RoleMapRepointModal vm={vm} />
            <RoleMapScopeDiffModal vm={vm} />
            <RoleMapAddModal vm={vm} />

            {/* EAA-1817: "Hoàn tác" after a delete — see `useRoleMapWrites.ts`'s
                docstring on `undoPending` for why this is a separate element
                rather than an action button riding inside `useToast()`'s
                notification (the shared `ToastApi` has no slot for one). No
                server-side expiry backs this window; it is a purely FRONTEND
                courtesy that stops offering the button after ~10s, not a claim
                that restoring becomes impossible after that. */}
            {vm.undo.pending && (
                <div className="rm-undo-banner" role="status">
                    <span className="rm-undo-banner__text">
                        {t('console.roleMap.listView.undoBannerText', {
                            label: vm.undo.pending.label,
                        })}
                    </span>
                    {/* EAA-1817: raw <button>s → kit Button; class names kept so the
                        undo banner's existing CSS still targets them. */}
                    <Button
                        className="rm-undo-banner__action"
                        onClick={vm.undo.restore}
                        disabled={vm.undo.busy}
                    >
                        {vm.undo.busy
                            ? t('console.roleMap.listView.undoRestoring')
                            : t('console.roleMap.listView.undoRestore')}
                    </Button>
                    <Button
                        className="rm-undo-banner__dismiss"
                        onClick={vm.undo.dismiss}
                        aria-label={t('console.roleMap.listView.undoDismissAria')}
                    >
                        ×
                    </Button>
                </div>
            )}
        </div>
    );
}

export default RoleMapListView;
